import { AvailablePaymentMethods } from "@stripe/stripe-js";
import { useExpressCheckoutContext } from "./ExpressCheckout.context";
import { ButtonMap } from "./ExpressCheckout.buttons";
import { IconButton, DropdownMenu } from "flicket-ui";
import { DotsThree } from "@phosphor-icons/react";
import { useState } from "react";
import { useRouter } from "next/router";
import { addQueryParams } from "~lib/helpers/queryParams";

export const ExpressCheckoutButtonRow = ({
  onClick,
  breakpoint,
  disabled = false,
}: {
  onClick: (paymentType: keyof AvailablePaymentMethods) => void;
  breakpoint: "xxs" | "xs";
  disabled?: boolean;
}) => {
  const ExpressCheckoutContext = useExpressCheckoutContext();
  const router = useRouter();

  // If we are not using express checkout then the provider will not be mounted
  if (!ExpressCheckoutContext) return null;

  const { availablePaymentMethods, hasPaymentMethods } = ExpressCheckoutContext;

  if (!hasPaymentMethods) return null;

  const firstTwoPayments = availablePaymentMethods?.slice(0, 2) || [];
  const remainingPayments = availablePaymentMethods?.slice(2) || [];
  const paymentCountOverflow = remainingPayments?.length > 0;

  function handleButtonClick(method: keyof AvailablePaymentMethods) {
    addQueryParams(router, {
      paymentType: method,
    });

    onClick?.(method);
  }

  return (
    <>
      {firstTwoPayments.map((method) => {
        const ButtonComponent = ButtonMap[method];
        return (
          <ButtonComponent
            key={method}
            onClick={() => handleButtonClick(method)}
            flex={1}
            maxW={{ [breakpoint]: "160px" }}
            disabled={disabled}
          />
        );
      })}

      {paymentCountOverflow && (
        <AdditionalButtonDropdown
          paymentMethods={remainingPayments}
          onClick={handleButtonClick}
        />
      )}
    </>
  );
};

function AdditionalButtonDropdown({
  paymentMethods,
  onClick,
}: {
  paymentMethods: (keyof AvailablePaymentMethods)[];
  onClick: (paymentType: keyof AvailablePaymentMethods) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger>
        <IconButton variant="solid" fontWeight="bold" isActive={open}>
          <DotsThree weight="bold" />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end">
        {paymentMethods.map((method) => {
          const ButtonComponent = ButtonMap[method];
          return (
            <DropdownMenu.CustomItem key={method} title="">
              <ButtonComponent
                onClick={() => onClick(method)}
                flex={1}
                maxW={{ xs: "160px" }}
                mx={1}
                my={"1/2"}
              />
            </DropdownMenu.CustomItem>
          );
        })}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
